import * as React from "react";
import { ArrowUpDown, Check, Filter, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SORT_OPTIONS } from "../lib/dashboard-filters";
import type { UseDashboardViewState } from "../hooks";
import type { DashboardDensity } from "@/types/dashboard.types";

interface Props {
  view: UseDashboardViewState;
  responsibles: string[];
  visibleIds: string[];
}

const MENU_ITEM =
  "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-[13px] text-text-secondary hover:bg-bg-muted-rd hover:text-text-primary";

const TRIGGER =
  "inline-flex h-8 items-center gap-1.5 rounded-md border border-border-subtle bg-bg-surface px-2.5 text-[12px] text-text-secondary hover:text-text-primary";

export function DashboardFilters({ view, responsibles, visibleIds }: Props): React.ReactElement {
  const searchRef = React.useRef<HTMLInputElement>(null);
  const sortLabel = SORT_OPTIONS.find((s) => s.id === view.sort)?.label ?? "Nome (A–Z)";

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {}
      <div className="relative flex h-8 items-center gap-2 rounded-md border border-border-subtle bg-bg-surface px-2.5">
        <Search className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" />
        <input
          ref={searchRef}
          value={view.query}
          onChange={(e) => view.setQuery(e.target.value)}
          placeholder="Buscar linha, produto…"
          aria-label="Buscar linha"
          className="w-48 bg-transparent text-[12px] outline-none placeholder:text-text-tertiary"
        />
        <kbd className="rounded border border-border-subtle bg-bg-app px-1.5 font-mono text-[10px] text-text-tertiary">
          /
        </kbd>
      </div>

      {}
      <Popover>
        <PopoverTrigger className={TRIGGER}>
          <Filter className="h-3 w-3" aria-hidden="true" />
          Responsável
          {view.responsibleFilter.size > 0 ? (
            <span className="rounded bg-brand-50 px-1 font-mono text-brand-700">
              {view.responsibleFilter.size}
            </span>
          ) : null}
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          {responsibles.length === 0 ? (
            <p className="p-2 text-[12px] text-text-tertiary">Sem responsáveis.</p>
          ) : (
            responsibles.map((r) => (
              <button
                key={r}
                type="button"
                className={MENU_ITEM}
                onClick={() => view.toggleResponsible(r)}
              >
                {r}
                {view.responsibleFilter.has(r) ? (
                  <Check className="h-3.5 w-3.5 text-brand-600" />
                ) : null}
              </button>
            ))
          )}
          {view.responsibleFilter.size > 0 ? (
            <button
              type="button"
              className={cn(MENU_ITEM, "text-text-tertiary")}
              onClick={view.clearResponsible}
            >
              Limpar filtro
            </button>
          ) : null}
        </PopoverContent>
      </Popover>

      {}
      <Popover>
        <PopoverTrigger className={TRIGGER}>
          <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
          {sortLabel}
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          {SORT_OPTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={MENU_ITEM}
              onClick={() => view.setSort(s.id)}
            >
              {s.label}
              {view.sort === s.id ? <Check className="h-3.5 w-3.5 text-brand-600" /> : null}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      {}
      <div className="inline-flex h-8 items-center overflow-hidden rounded-md border border-border-subtle bg-bg-surface p-[3px] text-[12px]">
        {(["compact", "expanded"] as DashboardDensity[]).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => view.setDensity(d, visibleIds)}
            aria-pressed={view.density === d}
            className={cn(
              "rounded-[5px] px-3 py-1 font-medium transition-colors",
              view.density === d
                ? "bg-brand-50 text-brand-700"
                : "text-text-secondary hover:text-text-primary"
            )}
          >
            {d === "compact" ? "Compacto" : "Expandido"}
          </button>
        ))}
      </div>
    </div>
  );
}
